import clsx from 'clsx';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KTIcon } from '../../../_metronic/helpers';
import {
  canAccessMenuItem,
  systemUserBottomMenuConfig,
  flattenMenuItems,
  systemUserMenuConfig,
  type MenuItemConfig,
} from '../../config/menuConfig';
import { systemPreferences, type SystemPreferenceOption } from '../../config/SettingsOptions';
import { useAbility } from '@/app/casl/AbilityContext';
import { type Resource } from '@/app/casl/resources';

interface MenuItem {
  title: string;
  description?: string;
  to: string;
  breadcrumb: string;
  permissions?: Resource[];
}

interface SystemSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

// Function to normalize strings by removing accents and special characters
const normalizeString = (str: string) => {
  return str
    .normalize('NFD') // Decomposes accented characters
    .replace(/[\u0300-\u036f]/g, '') // Removes diacritical marks
    .toLowerCase(); // Converts to lowercase
};

const SystemSearch: FC<SystemSearchProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState<MenuItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const ability = useAbility();

  // Flatten all menu items from config
  const allMenuItems = useMemo((): MenuItem[] => {
    const mapToMenuItem = (item: MenuItemConfig & { breadcrumb: string }): MenuItem => ({
      title: item.title,
      description: item.description,
      to: item.to,
      breadcrumb: item.breadcrumb,
      permissions: item.permissions,
    });

    const menuItems = [...flattenMenuItems(systemUserMenuConfig), ...flattenMenuItems(systemUserBottomMenuConfig)].map(
      mapToMenuItem,
    );

    const settingsItems: MenuItem[] = systemPreferences.flatMap((preference) =>
      preference.sections.flatMap((section) =>
        section.options.map((option: SystemPreferenceOption) => ({
          title: option.name,
          description: option.description,
          to: option.endpoint,
          breadcrumb: `Preferências > ${preference.name}${section.name ? ' > ' + section.name : ''} > ${option.name}`,
          permissions: option.permissions,
        })),
      ),
    );

    return [...menuItems, ...settingsItems];
  }, []);

  // Filter items based on search query and permissions
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredItems([]);
      setSelectedIndex(0);
      return;
    }

    const normalizedQuery = normalizeString(searchQuery);
    const filtered = allMenuItems.filter((item: MenuItem) => {
      // Check permissions first
      if (!canAccessMenuItem(item, ability)) {
        return false;
      }

      const normalizedTitle = normalizeString(item.title);
      const normalizedDescription = item.description ? normalizeString(item.description) : '';
      const normalizedBreadcrumb = item.breadcrumb ? normalizeString(item.breadcrumb) : '';
      // Check if title or description matches (with normalized strings)
      const titleMatch = normalizedTitle.includes(normalizedQuery);
      const descriptionMatch = normalizedDescription.includes(normalizedQuery);
      const breadcrumbMatch = normalizedBreadcrumb.includes(normalizedQuery);

      return titleMatch || descriptionMatch || breadcrumbMatch;
    });

    setFilteredItems(filtered);
    setSelectedIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, ability]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < filteredItems.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            handleNavigate(filteredItems[selectedIndex].to);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filteredItems, selectedIndex]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus input when opened
      inputRef.current?.focus();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleNavigate = (to: string) => {
    navigate(to);
    onClose();
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center"
      style={{
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        paddingTop: '10vh',
      }}
    >
      <div
        ref={searchRef}
        className="bg-body shadow-lg rounded"
        style={{
          width: '100%',
          maxWidth: '600px',
          maxHeight: '70vh',
          overflow: 'hidden',
        }}
      >
        {/* Search Input */}
        <div className="p-5 border-bottom">
          <div className="position-relative">
            <KTIcon className="fs-2 position-absolute top-50 translate-middle-y ms-4" iconName="magnifier" />
            <input
              ref={inputRef}
              autoComplete="off"
              className="form-control form-control-lg ps-14"
              placeholder="Buscar páginas..."
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Results */}
        <div className="overflow-auto" style={{ maxHeight: 'calc(60vh - 100px)' }}>
          {searchQuery && filteredItems.length === 0 ? (
            <div className="text-center py-10 text-muted">
              <KTIcon className="fs-3x mb-5" iconName="file-deleted" />
              <div className="fw-bold fs-4">Nenhum resultado encontrado</div>
              <div className="fs-7">Tente buscar com outros termos</div>
            </div>
          ) : searchQuery && filteredItems.length > 0 ? (
            <>
              <div className="px-5 pt-5 pb-2">
                <div className="text-muted text-uppercase fs-8 fw-bold">Sugestões ({filteredItems.length})</div>
              </div>
              {filteredItems.map((item, index) => (
                <div
                  key={item.to}
                  className={clsx('px-5 py-4 cursor-pointer', index === selectedIndex && 'bg-light-primary')}
                  onClick={() => handleNavigate(item.to)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <div className="fw-bold text-gray-800 fs-6 ek-link">{item.title}</div>
                      {item.description ? <div className="text-muted fs-7">{item.description}</div> : null}
                      {item.breadcrumb ? <div className="text-muted fs-8 mt-1">{item.breadcrumb}</div> : null}
                    </div>
                    {index === selectedIndex && <KTIcon className="fs-3 text-primary" iconName="arrow-right" />}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="text-center py-10 text-muted">
              <KTIcon className="fs-3x mb-5" iconName="search-list" />
              <div className="fw-bold fs-4">Comece a digitar para buscar</div>
              <div className="fs-7">Digite o nome de uma página ou funcionalidade</div>
            </div>
          )}
        </div>

        {/* Footer with keyboard hints */}
        {searchQuery && filteredItems.length > 0 ? (
          <div className="border-top px-5 py-4 bg-light">
            <div className="d-flex align-items-center justify-content-between fs-8 text-muted">
              <div>
                <kbd className="bg-primary border px-2 py-1 rounded me-1">↑</kbd>
                <kbd className="bg-primary border px-2 py-1 rounded me-2">↓</kbd>
                para navegar
              </div>
              <div>
                <kbd className="bg-primary border px-2 py-1 rounded me-2">Enter</kbd>
                para selecionar
              </div>
              <div>
                <kbd className="bg-primary border px-2 py-1 rounded">Esc</kbd>
                para fechar
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SystemSearch;
